import { useEffect, useState, useCallback } from 'react';
import { saveUTMsToCookies } from '@landr/core';
import useCustomEventController from 'controllers/CustomEvent';
import { usePostAuthenticationActionController } from 'controllers/PostAuthenticationAction';
import { useActivePlayerContext } from 'contexts/ActivePlayerContext/ActivePlayerContext';
import { useClientUpdateContext } from 'contexts/ClientUpdateContext/ClientUpdateContext';
import { useErrorBoundaryContext } from 'contexts/ErrorBoundaryContext/ErrorBoundaryContext';
import { useDeduplicateParams } from 'hooks/useDeduplicateParams';
import { useDisableIosTextFieldZoom } from 'hooks/useDisableIosTextFieldZoom';
import useMountEffect from 'hooks/useMountEffect';
import { useAuthentication } from 'contexts/AuthenticationProvider/AuthenticationProvider';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import { useDawPlugin } from 'hooks/useDawPlugin/useDawPlugin';
import { UTMCookieSettings } from './constants/UTMCookieSettings';

const useApp = () => {
    const { isDAWPlugin } = useExecutionEnvironmentContext();

    // STATE
    const [loaderDisplayed, setLoaderDisplayed] = useState(false);

    // CALLBACKS
    const onFallbackLoad = useCallback(
        (): void => setLoaderDisplayed(true),
        [],
    );
    const onFallbackUnload = useCallback(
        (): void => setLoaderDisplayed(false),
        [],
    );

    // CONTEXT
    const { stopActivePlayer } = useActivePlayerContext();

    const { setUpdateRequired } = useClientUpdateContext();

    const { error } = useErrorBoundaryContext();

    const { isAuthenticated, isLoading } = useAuthentication();

    // CONTROLLERS
    useCustomEventController({
        stopActivePlayer,
        setUpdateRequired,
    });

    usePostAuthenticationActionController({
        isAuthenticated,
        isAuthenticationLoading: isLoading,
    });

    // EFFECTS
    useMountEffect(() => {
        saveUTMsToCookies(UTMCookieSettings);
    });
    useDeduplicateParams();

    // To avoid bug https://mixgenius.atlassian.net/browse/SP-4106
    useDisableIosTextFieldZoom();

    // Required for Samples plugin to work
    useDawPlugin();

    useEffect(function preventContentReplacementOnAccidentalFileDrop() {
        const handleWindowDragOver = (e: DragEvent) => {
            e.preventDefault();
        };

        const handleWindowDrop = (e: DragEvent) => {
            e.preventDefault();
        };

        window.addEventListener('dragover', handleWindowDragOver);
        window.addEventListener('drop', handleWindowDrop);

        return () => {
            window.removeEventListener('dragover', handleWindowDragOver);
            window.removeEventListener('drop', handleWindowDrop);
        };
    }, []);

    return {
        isAuthenticated,
        isDAWPlugin,
        loaderDisplayed,
        onFallbackLoad,
        onFallbackUnload,
        error,
    };
};

export default useApp;
